import type { AxiosInstance } from 'axios'

import type {
  CausalNotificationTypeDto,
  CausalSubscriptionDto,
  CausalSubscriptionListDto,
  CausalSubscriptionWriteDto,
} from '../models/notification.types'
import { CAUSAL_NOTIFICATION_TYPE_NAME } from '../models/notification.types'

import { causalHttp } from './CausalApi'

import { ApiError, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

type CausalNotificationHttp = Pick<AxiosInstance, 'get' | 'post' | 'put' | 'delete'>

const FEATURE_OWNED_ERRORS = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
} as const

async function featureRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request
  } catch (cause) {
    throw ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }
}

/** Typed façade over the shared notification transport for causal execution schedules. */
export class CausalNotificationApi {
  constructor(private readonly http: CausalNotificationHttp = causalHttp) {}

  async resolveNotificationTypeId(): Promise<number | null> {
    const response = await featureRequest(
      this.http.get<ApiEnvelope<CausalNotificationTypeDto[]> | CausalNotificationTypeDto[]>(
        '/notifications/subscription/configs/',
        FEATURE_OWNED_ERRORS,
      ),
    )
    // The measured legacy producer returns a bare array; the migrated contract envelopes it.
    // Same dual-shape normalization as the sibling AlertApi.resolveNotificationTypeId() against
    // this exact endpoint.
    const body = response.data
    const configs = Array.isArray(body)
      ? body
      : unwrapEnvelope({ data: body as ApiEnvelope<CausalNotificationTypeDto[]> })
    return configs.find(({ name }) => name === CAUSAL_NOTIFICATION_TYPE_NAME)?.id ?? null
  }

  async list(workspaceId: number | string, typeId: number): Promise<CausalSubscriptionDto[]> {
    const response = await featureRequest(
      this.http.get<ApiEnvelope<CausalSubscriptionListDto>>(
        `/notifications/subscription/workspace/${workspaceId}/${typeId}/`,
        FEATURE_OWNED_ERRORS,
      ),
    )
    return unwrapEnvelope(response).subscriptions
  }

  async create(
    workspaceId: number | string,
    typeId: number,
    body: CausalSubscriptionWriteDto,
  ): Promise<CausalSubscriptionDto> {
    const response = await featureRequest(
      this.http.post<ApiEnvelope<CausalSubscriptionDto>>(
        `/notifications/subscription/${workspaceId}/${typeId}/`,
        body,
        FEATURE_OWNED_ERRORS,
      ),
    )
    return unwrapEnvelope(response)
  }

  async update(
    subscriptionId: number,
    body: CausalSubscriptionWriteDto,
  ): Promise<CausalSubscriptionDto> {
    const response = await featureRequest(
      this.http.put<ApiEnvelope<CausalSubscriptionDto>>(
        `/notifications/subscription/${subscriptionId}/update/`,
        body,
        FEATURE_OWNED_ERRORS,
      ),
    )
    return unwrapEnvelope(response)
  }

  async remove(subscriptionId: number): Promise<void> {
    const response = await featureRequest(
      this.http.delete<ApiEnvelope<unknown>>(
        `/notifications/subscription/${subscriptionId}/delete/`,
        FEATURE_OWNED_ERRORS,
      ),
    )
    unwrapEnvelope(response)
  }
}

export const causalNotificationApi = new CausalNotificationApi()
